/**
 * 🟥 CUADRO NEGRO T de M V2 — Detección + parseo de muestras multi-archivo.
 *
 * detectMultiFile(content): bool — devuelve true si el contenido tiene
 *   marcadores explícitos (// file:, # file:, <!-- file: -->) o múltiples
 *   code-fences etiquetados o múltiples headers `## archivo.ext`.
 *
 * parseMultiFile(content): [{path, content}] — extrae archivos del bloque
 *   usando los marcadores soportados. Si no hay marcadores claros pero hay
 *   múltiples code-fences etiquetados, divide por fence. Si no, devuelve
 *   un único archivo `index.txt` con el contenido entero (caller debería
 *   evitar llamarlo si detectMultiFile() es false).
 */

const FILE_MARKER_RE = /^(?:\/\/|#|<!--)\s*file:\s*(.+?)\s*(?:-->\s*)?$/i;
const HEADER_FILE_RE = /^##\s+(.+\.(?:jsx?|tsx?|html?|css|scss|py|json|md|yml|yaml|xml|sh|toml|rb|go|rs|java|kt|swift|php|vue))\s*$/i;
const FENCE_OPEN_RE = /^```([a-zA-Z0-9_+-]+)\s*$/;
const FENCE_CLOSE_RE = /^```\s*$/;

export function detectMultiFile(content) {
  if (!content || typeof content !== 'string') return false;
  const lines = content.split('\n');

  // Regla 1: marcadores explícitos
  for (const l of lines) {
    if (FILE_MARKER_RE.test(l.trim())) return true;
  }

  // Regla 2: múltiples code-fences etiquetados
  const fences = content.match(/^```[a-zA-Z0-9_+-]+\s*$/gm);
  if (fences && fences.length > 1) return true;

  // Regla 3: múltiples headers ## archivo.ext
  const headers = lines.filter((l) => HEADER_FILE_RE.test(l));
  if (headers.length > 1) return true;

  return false;
}

const EXT_FROM_LANG = {
  js: 'js', javascript: 'js', jsx: 'jsx',
  ts: 'ts', typescript: 'ts', tsx: 'tsx',
  html: 'html', css: 'css', scss: 'scss',
  py: 'py', python: 'py',
  json: 'json', md: 'md', markdown: 'md',
  yml: 'yml', yaml: 'yml', xml: 'xml',
  sh: 'sh', bash: 'sh',
  rb: 'rb', ruby: 'rb', go: 'go', rs: 'rs', rust: 'rs',
  java: 'java', kt: 'kt', swift: 'swift',
  php: 'php', vue: 'vue', toml: 'toml',
};

function sanitizePath(p) {
  if (!p) return '';
  return String(p)
    .trim()
    .replace(/\\/g, '/')
    .replace(/^\.\//, '')
    .replace(/^\/+/, '')
    .replace(/\.\.+/g, '.')
    .replace(/\s+/g, '_')
    .slice(0, 200);
}

export function parseMultiFile(content) {
  if (!content || typeof content !== 'string') return [];
  const lines = content.split('\n');

  // ESTRATEGIA 1 — marcadores `// file: x.ext` (precedencia más alta)
  const hasMarkers = lines.some((l) => FILE_MARKER_RE.test(l.trim()));
  if (hasMarkers) {
    const files = [];
    let current = null;
    for (const line of lines) {
      const m = line.trim().match(FILE_MARKER_RE);
      if (m) {
        if (current && current.content.trim().length > 0) files.push(current);
        current = { path: sanitizePath(m[1]), content: '' };
        continue;
      }
      if (current) current.content += line + '\n';
    }
    if (current && current.content.trim().length > 0) files.push(current);
    // Remover trailing fences ``` y limpiar.
    return files
      .map((f) => ({ ...f, content: stripFences(f.content) }))
      .filter((f) => f.path && f.content.trim().length > 0);
  }

  // ESTRATEGIA 2 — headers ## archivo.ext
  const hasHeaders = lines.filter((l) => HEADER_FILE_RE.test(l)).length > 1;
  if (hasHeaders) {
    const files = [];
    let current = null;
    for (const line of lines) {
      const h = line.match(HEADER_FILE_RE);
      if (h) {
        if (current && current.content.trim().length > 0) files.push(current);
        current = { path: sanitizePath(h[1]), content: '' };
        continue;
      }
      if (current) current.content += line + '\n';
    }
    if (current && current.content.trim().length > 0) files.push(current);
    return files
      .map((f) => ({ ...f, content: stripFences(f.content) }))
      .filter((f) => f.path && f.content.trim().length > 0);
  }

  // ESTRATEGIA 3 — múltiples code-fences etiquetados → un archivo por bloque.
  const fenceFiles = [];
  let inFence = false;
  let currentLang = '';
  let currentBuf = '';
  let idx = 1;
  for (const line of lines) {
    const open = line.match(FENCE_OPEN_RE);
    if (open && !inFence) {
      inFence = true;
      currentLang = (open[1] || '').toLowerCase();
      currentBuf = '';
      continue;
    }
    if (inFence && FENCE_CLOSE_RE.test(line)) {
      const ext = EXT_FROM_LANG[currentLang] || 'txt';
      fenceFiles.push({
        path: `file_${idx}.${ext}`,
        content: currentBuf,
      });
      idx += 1;
      inFence = false;
      currentLang = '';
      currentBuf = '';
      continue;
    }
    if (inFence) {
      currentBuf += line + '\n';
    }
  }
  if (fenceFiles.length > 0) {
    return fenceFiles.filter((f) => f.content.trim().length > 0);
  }

  // Fallback: todo en un solo archivo.
  return [{ path: 'index.txt', content }];
}

/** Elimina code-fences sueltos al principio/fin del contenido por archivo. */
function stripFences(s) {
  let out = (s || '').trim();
  out = out.replace(/^```[a-zA-Z0-9_+-]*\s*\n?/, '');
  out = out.replace(/\n?```\s*$/, '');
  return out;
}

export default detectMultiFile;
